import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { inquiryApi } from '../../../../api/endpoints';
import {format} from 'date-fns';
import {
  StickyFooter,
  Container,
  ProductInfo,
  ProductName,
  CatalogNumber,
  Dimensions,
  Actions,
  QuantitySection,
  QuantityLabel,
  QuantityInput,
  AddToCartButton,
  DeliveryInfo,
  DeliveryLabel,
  StockIndicator,
  DeliveryScheduleContainer,
  DeliveryScheduleIcon,
  DeliveryScheduleWrapper,
  DeliveryScheduleHeader,
  DeliveryScheduleTitle,
  DeliveryScheduleList,
  DeliveryScheduleItem,
  RefreshButton,
} from './TyreStickyFooter.style';
import formatCurrency from '../../../../lib/formatCurrency';

class TyreStickyFooter extends Component {
  static propTypes = {
    productName: PropTypes.string,
    catalogNumber: PropTypes.string.isRequired,
    dimensions: PropTypes.string,
    stock: PropTypes.number,
    grossPrice: PropTypes.number,
    onAddToCart: PropTypes.func.isRequired,
  };

  state = {
    quantity: 4,
    isAdding: false,
    scheduleDetails: [],
    loadingDelivery: true,
    isNotInStock: false,
    notInStockQuantity: null,
  };

  componentDidMount() {
    this.fetchDeliveryDate();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.catalogNumber !== this.props.catalogNumber) {
      this.fetchDeliveryDate();
    }
  }

  fetchDeliveryDate = async () => {
    const {catalogNumber} = this.props;
    const {quantity} = this.state;

    this.setState({loadingDelivery: true, scheduleDetails: [], isNotInStock: false, notInStockQuantity: null});

    try {
      const response = await inquiryApi({
        inquiry: {
          orderLines: [
            {
              catalogNumber,
              rowQuantity: quantity,
            },
          ],
        },
      });

      // Parse response based on SearchContainer structure
      if (response?.OrderLine?.[0]?.OrderedArticle?.[0]?.ScheduleDetails) {
        const scheduleDetailsRaw =
          response.OrderLine[0].OrderedArticle[0].ScheduleDetails;

        if (scheduleDetailsRaw && scheduleDetailsRaw.length > 0) {
          // Parse all schedule details
          let notInStockQty = 0;
          const parsedSchedules = scheduleDetailsRaw
            .map((detail) => {
              const deliveryDateValue = detail.DeliveryDate?.[0];
              const quantityValue = detail.AvailableQuantity?.[0]?.QuantityValue?.[0];

              if (deliveryDateValue && quantityValue) {
                const formatted = format(new Date(deliveryDateValue), 'dd.MM.yyyy');
                const qty = parseInt(quantityValue, 10);
                // Check for invalid dates (31.12.9999) - means not in stock
                if (formatted === '31.12.9999') {
                  notInStockQty += qty;
                  return null;
                }
                return {
                  date: formatted,
                  quantity: qty,
                };
              }
              return null;
            })
            .filter(Boolean);

          if (parsedSchedules.length > 0) {
            this.setState({
              scheduleDetails: parsedSchedules, 
              loadingDelivery: false, 
              isNotInStock: false,
              notInStockQuantity: notInStockQty > 0 ? notInStockQty : null,
            });
            return;
          }

          // If we had schedule details but all were 9999-12-31, mark as not in stock
          if (notInStockQty > 0) {
            this.setState({loadingDelivery: false, isNotInStock: true, notInStockQuantity: notInStockQty});
            return;
          }
        }
      }

      this.setState({loadingDelivery: false});
    } catch (error) {
      console.error('Error fetching delivery date:', error);
      this.setState({loadingDelivery: false});
    }
  };

  handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    const newQuantity = Math.max(1, value);
    this.setState({quantity: newQuantity}, () => {
      // Refetch delivery date when quantity changes
      this.fetchDeliveryDate();
    });
  };

  handleAddToCart = async () => {
    const {onAddToCart, catalogNumber} = this.props;
    const {quantity, scheduleDetails, isNotInStock, notInStockQuantity} = this.state;

    // Use the first delivery date for cart (or null if none)
    const deliveryDate = scheduleDetails.length > 0 ? scheduleDetails[0].date : null;

    this.setState({isAdding: true});
    try {
      await onAddToCart(catalogNumber, quantity, deliveryDate, scheduleDetails, isNotInStock, notInStockQuantity);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.setState({isAdding: false});
    }
  };

  getStockStatus = () => {
    const {stock, t} = this.props;

    if (stock === undefined || stock === null) {
      return {text: t('notInStock'), color: 'red'};
    }

    if (stock === 0) {
      return {text: t('sendInquiry'), color: 'red'};
    }

    if (stock >= 1 && stock <= 3) {
      return {
        text: t('stockCount', { count: stock }),
        color: 'orange',
      };
    }

    if (stock >= 4) {
      return {text: t('stockFourPlus'), color: 'green'};
    }

    return {text: t('notInStock'), color: 'red'};
  };

  render() {
    const {productName, catalogNumber, dimensions, grossPrice, t} = this.props;
    const {quantity, isAdding, scheduleDetails, loadingDelivery, isNotInStock, notInStockQuantity} = this.state;
    const stockStatus = this.getStockStatus();

    return (
      <StickyFooter>
        <Container>
          <ProductInfo>
            <ProductName>{productName || catalogNumber}</ProductName>
            <CatalogNumber>#{catalogNumber}</CatalogNumber>
            {dimensions && <Dimensions>{dimensions}</Dimensions>}
            {grossPrice !== undefined && grossPrice !== null && (
              <Dimensions style={{fontWeight: 'bold', color: '#e8590c'}}>
                {formatCurrency(grossPrice)}
              </Dimensions>
            )}
          </ProductInfo>

          <Actions>
            <DeliveryInfo>
              <StockIndicator color={stockStatus.color}>
                <i className="mdi mdi-package-variant" />
                {stockStatus.text}
              </StockIndicator>
              {loadingDelivery ? (
                <DeliveryLabel>
                  <i className="mdi mdi-loading mdi-spin" />
                  {t('loading')}...
                </DeliveryLabel>
              ) : isNotInStock ? (
                <DeliveryLabel style={{color: '#dc3545'}}>
                  <i className="mdi mdi-package-variant-remove" />
                  {t('notInStock')}
                </DeliveryLabel>
              ) : scheduleDetails.length > 0 ? (
                <DeliveryScheduleContainer>
                  <DeliveryScheduleIcon>
                    <i className="mdi mdi-truck-delivery" />
                  </DeliveryScheduleIcon>
                  <DeliveryScheduleWrapper>
                    <DeliveryScheduleHeader>
                      <DeliveryScheduleTitle>{t('expectedDeliveryDate')}</DeliveryScheduleTitle>
                      <RefreshButton
                        onClick={this.fetchDeliveryDate}
                        disabled={loadingDelivery}
                        className={loadingDelivery ? 'spinning' : ''}
                        title={t('inquiry')}
                      >
                        <i className="mdi mdi-refresh" />
                      </RefreshButton>
                    </DeliveryScheduleHeader>
                    <DeliveryScheduleList>
                      {scheduleDetails.map((schedule, index) => (
                        <DeliveryScheduleItem key={index}>
                          <span className="quantity">{schedule.quantity} {t('pcs')}</span>
                          <span className="separator">→</span>
                          <span className="date">{schedule.date}</span>
                        </DeliveryScheduleItem>
                      ))}
                      {notInStockQuantity > 0 && (
                        <DeliveryScheduleItem style={{color: '#dc3545'}}>
                          <span className="quantity" style={{color: '#dc3545'}}>{notInStockQuantity} {t('pcs')}</span>
                          <span className="separator" style={{color: '#dc3545'}}>→</span>
                          <span className="date" style={{color: '#dc3545'}}>{t('notInStock')}</span>
                        </DeliveryScheduleItem>
                      )}
                    </DeliveryScheduleList>
                  </DeliveryScheduleWrapper>
                </DeliveryScheduleContainer>
              ) : null}
            </DeliveryInfo>

            <QuantitySection>
              <QuantityLabel>{t('quantity')}:</QuantityLabel>
              <QuantityInput
                type="number"
                min="1"
                value={quantity}
                onChange={this.handleQuantityChange}
                disabled={isAdding}
              />
            </QuantitySection>

            <AddToCartButton
              onClick={this.handleAddToCart}
              disabled={isAdding || stockStatus.color === 'red'}
            >
              {isAdding ? (
                <>
                  <i className="mdi mdi-loading mdi-spin" />
                  {t('adding')}
                </>
              ) : (
                <>
                  <i className="mdi mdi-cart-plus" />
                  {t('addToCart')}
                </>
              )}
            </AddToCartButton>
          </Actions>
        </Container>
      </StickyFooter>
    );
  }
}

export default withTranslation()(TyreStickyFooter);
